import React, { ReactNode } from "react"
import { Box } from "@chakra-ui/react"

export type DeviceFrameSpec = {
  frameSrc: string
  screen: { x: number, y: number, width: number, height: number }
  frameSize: { width: number, height: number }
  /** CSS border radius for the visible screen area (e.g. "48px" or "6%") */
  screenRadius?: number
}

type Props = {
  spec: DeviceFrameSpec
  /** CSS width of the whole frame (e.g. "620px") */
  w: string
  children: ReactNode
}

const PX_WIDTH = /^(\d+(?:\.\d+)?)px$/

const DeviceFrame = ({ spec, w, children }: Props) => {
  const {
    frameSrc,
    screen,
    frameSize,
  } = spec

  const widthPxMatch = PX_WIDTH.exec(w)
  const frameOuterHeightPx = widthPxMatch !== null
    ? Number(widthPxMatch[1]) * (frameSize.height / frameSize.width)
    : undefined

  const leftPct = (screen.x / frameSize.width) * 100
  const topPct = (screen.y / frameSize.height) * 100
  const widthPct = (screen.width / frameSize.width) * 100
  const heightPct = (screen.height / frameSize.height) * 100
  // Pin all four edges so subpixel rounding cannot leave a 1px seam.
  const rightPct = 100 - leftPct - widthPct
  const bottomPct = 100 - topPct - heightPct

  return (
    <Box
      position="relative"
      w={w}
      maxW="100%"
      h={frameOuterHeightPx !== undefined ? `${frameOuterHeightPx}px` : undefined}
      aspectRatio={
        frameOuterHeightPx !== undefined ? undefined : frameSize.width / frameSize.height
      }
    >
      <Box
        position="absolute"
        left={`calc(${leftPct}% - 1px)`}
        top={`calc(${topPct}% - 1px)`}
        right={`calc(${rightPct}% - 1px)`}
        bottom={`calc(${bottomPct}% - 1px)`}
        w="auto"
        h="auto"
        overflow="hidden"
        borderRadius={spec.screenRadius ?? undefined}
        bg="black"
        transform="translateZ(0)"
      >
        {children}
      </Box>
      <Box
        as="img"
        src={frameSrc}
        alt=""
        aria-hidden="true"
        draggable={false}
        position="absolute"
        inset={0}
        w="100%"
        h="100%"
        objectFit="contain"
        pointerEvents="none"
        userSelect="none"
        style={{ filter: "drop-shadow(0 18px 45px rgba(0,0,0,0.25))" }}
      />
    </Box>
  )
}

export default DeviceFrame
